import { useEffect } from 'react';
import { z } from 'zod';
import { criarErpClient, type ErpClient } from '../erpClient';

/**
 * Polling de `GET /api/erp/GetStatusSistema` (T027, `FR-013`, AD-088).
 *
 * Mecanismo secundário, sem relação com o envio da venda: verifica, **só entre
 * vendas**, se a configuração publicada em `GetSessao` mudou. Vive nesta
 * feature porque é onde a spec formal posicionou o requisito, não por afinidade
 * de domínio (`research.md`, D6).
 *
 * `Empresa` não é montado aqui: o BFF o injeta como header em toda chamada
 * `/api/erp/*` (AD-019/AD-118). O cliente só envia `Cadmaqcod`.
 */

const CAMINHO_STATUS_SISTEMA = '/ApiCentriumOAuth/GetStatusSistema';

/** 60 segundos (AD-088) — a única atividade periódica desta feature. */
export const INTERVALO_STATUS_SISTEMA_MS = 60_000;

/**
 * A resposta é um inteiro — mas **nem sempre escalar**.
 *
 * `contracts/status-sistema-api.md` e o YAML descrevem um inteiro puro no corpo,
 * e é isso que o `erp-mock` devolve. O ERP real devolve `{"Status": 0}`
 * (verificado ao vivo em 2026-09-04 — AD-165): com o schema escalar anterior o
 * parse falhava sempre, `consultarStatusSistema` retornava `null` em toda
 * consulta e o rebootstrap de `FR-013`/AD-088 **nunca** disparava contra o ERP
 * real — uma falha silenciosa, porque falha de status por desenho não vira erro
 * visível.
 *
 * As duas formas são aceitas e reduzidas ao mesmo inteiro. Validado na fronteira
 * mesmo sendo escalar: um corpo inesperado não pode virar `NaN >= 1` e disparar
 * um rebootstrap do nada (Constitution IV).
 */
const statusSistemaSchema = z.union([
  z.number().int(),
  z.looseObject({ Status: z.number().int() }).transform((corpo) => corpo.Status),
]);

export interface StatusSistemaDeps {
  readonly erpClient?: ErpClient;
  /** `SessaoUsuario.CadMaqCod`; `null` enquanto o bootstrap não carregou. */
  readonly cadMaqCod: () => string | null;
  /**
   * Venda em digitação: carrinho com pelo menos 1 item **ou** cliente já
   * identificado (features 003 e 005). Lido, nunca mutado.
   */
  readonly vendaAtiva: () => boolean;
  /** `refetchBootstrap()` da feature 002 — recarrega `SessaoUsuario` inteiro. */
  readonly recarregarBootstrap: () => void;
  readonly intervaloMs?: number;
}

/** `null` = nada a decidir (rede, sessão encerrada ou corpo inesperado). */
export async function consultarStatusSistema(
  cadMaqCod: string,
  deps: Pick<StatusSistemaDeps, 'erpClient'> = {},
): Promise<number | null> {
  const cliente = deps.erpClient ?? criarErpClient();
  const query = new URLSearchParams({ Cadmaqcod: cadMaqCod });

  const resultado = await cliente.chamar(`${CAMINHO_STATUS_SISTEMA}?${query.toString()}`, {
    method: 'GET',
  });

  // Falha de rede aqui não é evento: o ciclo simplesmente tenta de novo daqui a
  // 60s. Diferente de `FaturarNFCe`, isto não é operação crítica e não deve
  // produzir estado de erro visível ao operador
  // (`contracts/status-sistema-api.md`, § Erros).
  if (resultado.estado !== 'ok' || !resultado.resposta.ok) {
    return null;
  }

  let corpo: unknown;
  try {
    corpo = await resultado.resposta.json();
  } catch {
    return null;
  }

  const validado = statusSistemaSchema.safeParse(corpo);
  return validado.success ? validado.data : null;
}

/**
 * Liga o polling enquanto **não** há venda em andamento.
 *
 * O intervalo é criado e destruído conforme a guarda, em vez de rodar sempre e
 * ignorar o tick durante a venda: uma chamada supérflua a cada 60s no meio de
 * uma venda ativa continuaria consumindo rede e sessão sem nenhum uso
 * (`data-model.md` §6, "Suspenso — intervalo pausado, não só ignorado").
 */
export function usePollingStatusSistema(deps: StatusSistemaDeps): void {
  const { cadMaqCod, vendaAtiva, recarregarBootstrap, erpClient } = deps;
  const intervaloMs = deps.intervaloMs ?? INTERVALO_STATUS_SISTEMA_MS;

  const codigo = cadMaqCod();
  const ativo = codigo !== null && !vendaAtiva();

  useEffect(() => {
    if (!ativo || codigo === null) {
      return;
    }

    let cancelado = false;

    const ciclo = async (): Promise<void> => {
      const status = await consultarStatusSistema(
        codigo,
        erpClient === undefined ? {} : { erpClient },
      );
      if (cancelado) {
        return;
      }
      // `0` = nada mudou. Qualquer valor `>= 1` significa "algo mudou"; o
      // significado específico acima de 1 não importa para esta decisão binária
      // (AD-075/AD-080/AD-088).
      if (status !== null && status >= 1) {
        recarregarBootstrap();
      }
    };

    const temporizador = setInterval(() => {
      void ciclo();
    }, intervaloMs);

    return () => {
      cancelado = true;
      clearInterval(temporizador);
    };
  }, [ativo, codigo, erpClient, intervaloMs, recarregarBootstrap]);
}
